import { Container, Pending } from '@cluey/cluey-components';
import classNames from 'classnames';
import { isNull } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';

import withHeadTag from '../../../common/HeadComponent';
import HubPageLayout from '../../../common/hub/HubPageLayout';
import DropdownMenu, { DropdownMenuItem } from '../../../components/DropdownMenu/DropdownMenu';
import { Loader } from '../../../components/Loader/Loader';
import PillButtons, { PillButtonOption } from '../../../components/PillButtons/PillButtons';
import { SkeletonLoader } from '../../../components/SkeletonLoader';
import PageHead from '../../../components/hub/PageHead';
import { formatError } from '../../../util/errors';
import { PATH_HUB_ACCOUNT_DETAILS, PATH_HUB_INVOICE_HISTORY, PATH_HUB_PLAN_DETAILS } from '../../../util/pagePath';
import { ClueyPlus } from './ClueyPlus/Subscription';
import useSubscriptionContext, { withSubscriptionContextProvider } from './useSubscriptionContext';

const Subscriptions = () => {
  const history = useHistory();
  const {
    clueyPlus: { getSubscription, isLoading: isLoadingClueyPlus },
    banner: { show: showBanner, message: bannerMessage },
    student: {
      students,
      studentIsLoading,
      studentError,
      selectedStudent,
      setSelectedStudent,
      selectedStudentEnrolments,
    },
  } = useSubscriptionContext();

  const studentsList: PillButtonOption[] = useMemo(() => {
    if (!studentIsLoading) {
      return [...(students ?? [])].map((student) => {
        const showLastName = student.lastName && student.lastName.toLowerCase() !== '[not provided]';
        return {
          id: student.studentId,
          label: `${student.firstName} ${showLastName ? student.lastName : ''}`,
          subLabel: student.yearLevel,
        };
      });
    }
    return [];
  }, [students, studentIsLoading]);

  useEffect(() => {
    if (!studentIsLoading && students?.length) {
      setSelectedStudent(students[0] ?? null);
    }
  }, [students, studentIsLoading, setSelectedStudent]);

  const error = studentError ? formatError(studentError) : undefined;

  const isLoading = studentIsLoading || isLoadingClueyPlus;

  const SUBSCRIPTION_HEADER_MENU: DropdownMenuItem[] = [
    { label: 'View plan details', onClick: () => history.push(PATH_HUB_PLAN_DETAILS) },
    { label: 'Update contact details', onClick: () => history.push(PATH_HUB_ACCOUNT_DETAILS) },
    { label: 'Update payment details', onClick: () => history.push(PATH_HUB_INVOICE_HISTORY) },
    { label: 'View invoice history', onClick: () => history.push(PATH_HUB_INVOICE_HISTORY) },
  ];

  return (
    <HubPageLayout noContainer showFooterBorder>
      <div className="poppins w-full border-b-2 border-grey-2 bg-blood-orange-1">
        <Container>
          <div className="relative w-full pt-5 lg:pt-11">
            <PageHead
              title="Subscriptions"
              containerClasses="lg:!items-start"
              titleClasses="!border-b-0 lg:!text-[32px] !font-[merriweather]"
              wrap
            >
              <div className="flex">
                <DropdownMenu
                  label="Manage account"
                  items={SUBSCRIPTION_HEADER_MENU}
                  className="w-full"
                  buttonClassName="w-full text-left border border-grey-4 !px-4 rounded-full bg-white"
                  labelClassName="!font-medium !text-base color-grey-4"
                  itemClassName="!py-3 !px-5 rounded-lg"
                  dropdownClassName="lg:!w-60"
                />
              </div>
            </PageHead>
            <div className="flex flex-col gap-3">
              <Pending
                loading={isLoading}
                error={studentError ? formatError(studentError) : undefined}
                loader={
                  <div className="flex flex-row gap-x-4 py-3">
                    <SkeletonLoader loading={isLoading} className="h-12 w-32 !rounded-full" />
                    <SkeletonLoader loading={isLoading} className="h-12 w-32 !rounded-full" />
                    <SkeletonLoader loading={isLoading} className="h-12 w-32 !rounded-full" />
                  </div>
                }
              >
                <PillButtons
                  options={studentsList}
                  selectedId={selectedStudent?.studentId ?? null}
                  titleClassName="font-semibold"
                  selectedButtonClassName="bg-[#01220D] border-[#01220D] text-white"
                  selectedSubtitleClassName="text-blood-orange-4"
                  onSelect={(id) => {
                    setSelectedStudent(students?.find((student) => student.studentId === id) ?? null);
                  }}
                />
              </Pending>
              {showBanner && <>{bannerMessage}</>}
              {isNull(getSubscription()) && selectedStudentEnrolments.length > 0 ? (
                <div
                  className={classNames(
                    'mb-10 flex w-full flex-col rounded-3xl border-2 border-dashed border-grey-2 bg-white p-6 lg:flex-row lg:p-10'
                  )}
                >
                  <b className="text-lg font-semibold">No subscriptions yet</b>
                </div>
              ) : (
                <div
                  className={classNames(
                    'mb-10 flex w-full flex-col gap-x-10 gap-y-6 rounded-3xl bg-white p-6 lg:flex-row lg:p-10',
                    isLoading && 'justify-center'
                  )}
                >
                  <Pending
                    loading={isLoading}
                    error={error}
                    loader={<Loader className="flex min-h-[100px] items-center justify-center" />}
                  >
                    <ClueyPlus selectedStudent={selectedStudent} />
                  </Pending>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
    </HubPageLayout>
  );
};

export default withSubscriptionContextProvider(withHeadTag(Subscriptions));
